import { RefObject, useEffect, useRef, useState } from "react";

import { InfinitePaginatedPromise } from "@/types/InfiniteQuery.type";

export type UseInfiniteScrollProps<T> = {
  containerRef: RefObject<HTMLElement>;
  hasNextPage: boolean;
  fetchNextPage?: () => InfinitePaginatedPromise<T>;
};

const useInfiniteScroll = <T>({ containerRef, hasNextPage, fetchNextPage }: UseInfiniteScrollProps<T>) => {
  const observerRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleFetchNextPage = async () => {
      if (fetchNextPage) {
        setLoading(true);
        await fetchNextPage();
        setLoading(false);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !loading) {
          handleFetchNextPage();
        }
      },
      {
        root: containerRef.current,
        rootMargin: "600px",
        threshold: 0.1,
      },
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [fetchNextPage, hasNextPage, loading]);

  return observerRef;
};

export default useInfiniteScroll;
